import { getItems, getItemsForDropdown } from '../../Services/api.service';
import styles from './dropdown.module.scss';
import { useEffect, useRef, useState } from 'react';

const Dropdown = (props) => {
    const [dropdownItems, setDropdownItems] = useState([]);
    const [customFilteredItemList, setCustomFilteredItemList] = useState([]);
    const [totalNumberOfItems, setTotalNumberOfItems] = useState(0);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [displayDropDownItems, setDisplayDropDownItems] = useState(false);
    const [displayCustomDropdown, setDisplayCustomDropdown] = useState(false);
    const [displayExisting, setDisplayExisting] = useState(false);
    const [kwargs, setKwargs] = useState('');
    const loadMoreRef = useRef(null); // Ref to the "load more" element

    useEffect(() => {
        let defaultText = '';
        (async () => {
            if (props.objectType.includes("custom")) {
                setDisplayCustomDropdown(true);
                const customList = customListMap[props.objectType.split('custom:')[1]];
                setDropdownItems(customList);
                setCustomFilteredItemList(customList);
                setTotalNumberOfItems(customList.length);
                defaultText = props.value;
                if (props.value === -1) {
                    defaultText = customList[0]["id"];
                    props.onChange({ target: { value: customList[0]["value"] } });
                };
            }
            else {
                setFetchLoading(true);
                try {
                    const result = props.value === -1 ? await getItemsForDropdown(props.objectType) : await getItemsForDropdown(props.objectType, props.value);
                    setDropdownItems(result.results || []);
                    setTotalNumberOfItems(result.count);
                    if (props.value !== -1) {
                        setDisplayExisting(true);
                        const item = result.results.find(i => i.id === props.value);
                        defaultText = handleDisplayKey(item);
                    };   
                } catch (error) {
                    console.error("Error fetching next page:", error); // Handle any errors during fetch
                } finally {
                    setFetchLoading(false); // Set loading state to false after the data is fetched
                }
            }

            const element = document.getElementById(`searchValue-${props.label}`);

            if (element) {
                element.value = defaultText;
            }

        })();
    // eslint-disable-next-line
    }, [props.objectType]);

    useEffect(() => {
        if (!displayDropDownItems || !loadMoreRef.current || dropdownItems.length >= totalNumberOfItems || displayCustomDropdown) return; // Wait until dropdown is visible

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !fetchLoading) {
                        fetchNextPage();
                    }
                });
            },
            {
                root: document.querySelector(`.${styles.dropdownItemContainer}`), // Ensure the correct scroll container
                rootMargin: '0px',
                threshold: 0.8,
            }
        );
    
        observer.observe(loadMoreRef.current);
    
        return () => {
            observer.disconnect(); // Cleanup when dropdown is closed
        };
    // eslint-disable-next-line
    }, [displayDropDownItems, dropdownItems]);
    
    const fetchNextPage = async () => {
        setFetchLoading(true);
        try {
            const nextPage = await getItems(props.objectType, false, undefined, undefined, false);
            setDropdownItems(nextPage.results);
        } catch (error) {
            console.error("Error fetching next page:", error); // Handle any errors during fetch
        } finally {
            setFetchLoading(false); // Set loading state to false after the data is fetched
        }
    };

    const handleDisplayKey = (obj) => {
        const items = props.displayKey;
        if (!Array.isArray(items) || items.length === 0 || items.length > 2) {
            throw new Error("Input must be an array with one or two items.");
        }
    
        const values = items
            .map(item => obj[item])
            .filter(value => value !== undefined && value !== null && value !== '');
    
        return values.join(' - ');
    };

    let dataRender;
    if (dropdownItems) {
        let currentList = dropdownItems;
        if (displayCustomDropdown) {
            currentList = customFilteredItemList;
        }

        dataRender = currentList.map((i) => (
            <div key={i.id} className={styles.dropdownItem} onClick={() => handleSelectClick(i)}>
                {handleDisplayKey(i)}
            </div>
        ));
    }

    useEffect(() => {
        if (displayCustomDropdown) return;

        setFetchLoading(true);

        if (kwargs !== '') {
            const delaySearch = setTimeout(async () => {
                try {
                    // Make the search request when kwargs is not empty
                    const searchPage = await getItems(props.objectType, false, 1, kwargs);
                    setTotalNumberOfItems(searchPage.count);
                    setDropdownItems(searchPage.results);
                } catch (error) {
                    console.error('Error fetching search results:', error);
                } finally {
                    setFetchLoading(false);
                }
            }, 800); // Waits 800ms before triggering search

            return () => clearTimeout(delaySearch); // Cleanup to cancel previous call if user types again
        }

        if (kwargs === "" && displayExisting) {
            const delaySearch = setTimeout(async () => {
                try {
                    const resetPage = await getItems(props.objectType, false, 1, undefined, true);
                    setTotalNumberOfItems(resetPage.count);
                    setDropdownItems(resetPage.results);
                } catch (error) {
                    console.error(`Error fetching search results for ${props.objectType}:`, error);
                } finally {
                    setFetchLoading(false);
                }
            }, 800); // Waits 800ms before triggering search

            return () => clearTimeout(delaySearch); // Cleanup to cancel previous call if user types again
        }
    // eslint-disable-next-line
    }, [kwargs, displayCustomDropdown]);

    useEffect(() => {
        if (!displayCustomDropdown) return;

        const filtered = dropdownItems.filter(item =>
            item.value.toLowerCase().includes(kwargs.toLowerCase())
        );

        setCustomFilteredItemList(filtered);
        setTotalNumberOfItems(filtered.length);
    // eslint-disable-next-line
    }, [kwargs]);

    const handleSelectClick = (item) => {
        document.getElementById(`searchValue-${props.label}`).value = handleDisplayKey(item);
        handleSearchClick();
        if (props.onChange) {
            props.onChange({ target: { value: item.id ? item.id : item.value } });
        }
    }

    const handleSearchClick = () => {
        setDisplayDropDownItems(prev => !prev); // Update state after return is skipped
    };

    return (
        <>
            <div className={styles.dropdownContainer}>
                <p>
                    {props.required ? '*' : null}
                    <b>{props.label}</b>
                </p>
                
                <input
                    autoComplete="off"
                    id={`searchValue-${props.label}`}
                    placeholder='Select'
                    onChange={(e) => setKwargs(e.target.value)}
                    onClick={() => handleSearchClick()}
                    disabled={props.disabled}
                />

                {displayDropDownItems ?
                <>
                    <div className={styles.backDrop} onClick={() => handleSearchClick()} />
                    {totalNumberOfItems > 0 ?
                        <>
                            <div className={styles.dropdownItemContainer}>
                                {dataRender}
                                {displayCustomDropdown ? null :
                                    <div ref={loadMoreRef} style={{ width: '100%', height: '50px', textAlign: 'center'}}>
                                        {fetchLoading ? <p>Loading..</p> : <p>You've reached the end!</p>}
                                    </div>
                                }
                            </div>
                        </>
                    :
                        null
                    }
                </>
                :
                    null
                }
            </div>
        </>
    );
};

export default Dropdown;

const EMAIL_LIST = [
    {
        "id": "yasir@clearservices.ca",
        "value": "yasir@clearservices.ca"
    }
]

const SERVICE_TYPES = [
    {
        "id": "Janitorial",
        "value": "Janitorial"
    },
    {
        "id": "Contracting",
        "value": "Contracting"
    }
]

const STATUS_TYPES = [
    {
        "id": "Scheduled",
        "value": "Scheduled"
    },
    {
        "id": "Complete",
        "value": "Complete"
    },
    {
        "id": "Cancelled",
        "value": "Cancelled"
    }
]

const customListMap = {
    SERVICE_TYPES: SERVICE_TYPES,
    STATUS_TYPES: STATUS_TYPES,
    EMAIL_LIST: EMAIL_LIST
}